import {
  Component, ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { first, forkJoin, Observable } from 'rxjs';
import { catchError, last, map, tap } from 'rxjs/operators';

import { selectCurrentSite } from '../../../features/site/site.selectors';
import { Site } from '../../../models/site';
import { ApiService } from '../../../services/api.service';


@Component({
    selector: 'upload-button',
    templateUrl: './upload-button.component.html',
    styleUrls: ['./upload-button.component.scss'],
    standalone: false
})
export class UploadButtonComponent {
  progress = 0;
  isLoading = false;
  selectedFiles: File[] = [];
  @Input() disabled: boolean = false;
  @Output() uploadCompleted = new EventEmitter<string[]>();
  @Output() uploadFailed = new EventEmitter<any>();

  @ViewChild('filesUpload') filesUploadRef: ElementRef<HTMLInputElement> | undefined;

  private totalBytes = 0;
  private uploadedBytesByFile: Record<string, number> = {};

  constructor(
    private store: Store,
    private api: ApiService,
  ) {}

  reset(): void {
    if (this.filesUploadRef) {
      this.filesUploadRef.nativeElement.value = '';
      this.selectedFiles = [];
    }
  }

  openImageFilesChooser(input: HTMLInputElement): void {
    input.click();
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files || input.files.length === 0) {
      return;
    }

    this.selectedFiles = Array.from(input.files);

    // reset pour pouvoir re-choisir les mêmes fichiers ensuite
    input.value = '';
  }

  public startUpload(): void {
    if (!this.selectedFiles.length) {
      return;
    }

    this.upload(this.selectedFiles);
  }

  public clearFiles(): void {
    this.selectedFiles = [];
    this.progress = 0;
    this.totalBytes = 0;
    this.uploadedBytesByFile = {};
  }

  private upload(files: File[]): void {
    this.store.select(selectCurrentSite)
      .pipe(first())
      .subscribe({
        next: (site: Site | null | undefined) => {
          if (!site) {
            this.isLoading = false;
            return;
          }

          this.isLoading = true;
          this.progress = 0;
          this.totalBytes = files.reduce((sum, file) => sum + file.size, 0);
          this.uploadedBytesByFile = {};

          files.forEach(file => {
            this.uploadedBytesByFile[UploadButtonComponent.getFileKey(file)] = 0;
          });

          const fileNames = files.map(file => file.name);

          this.api.requestSiteExtraPresignedUrlPut$(site.organization, site.id, fileNames)
            .pipe(first())
            .subscribe({
              next: (data) => this.handlePresignedUrlPutSuccess(data, site, files),
              error: (e) => this.handleError(e)
            });
        },
        error: (e) => this.handleError(e)
      });
  }

  private handlePresignedUrlPutSuccess(data: any, site: Site, files: File[]): void {
    const urlsByFileName: Record<string, string> = data ?? {};

    const uploads$: Observable<any>[] = files.map((file: File) => {
      const url = urlsByFileName[file.name];

      if (!url) {
        throw new Error(`Aucune URL d'upload trouvée pour ${file.name}`);
      }

      return this.api.uploadFilePut$(url, {}, file).pipe(
        tap((event: HttpEvent<any>) => this.handleUploadEvent(event, file)),
        map((event: HttpEvent<any>) => UploadButtonComponent.eventMessage(event, file)),
        tap(file => file),
        last(),
        catchError((e) => {
          this.handleError(e);
          throw e;
        })
      );
    });

    forkJoin(uploads$).subscribe({
      next: (uploadedFiles) => {
        this.progress = 100;
        this.isLoading = false;
        this.uploadCompleted.emit(
          uploadedFiles.map(file => file.name)
        );
      },
      error: (e) => {
        this.handleError(e);
        this.uploadFailed.emit(e);
      }
    });
  }

  private handleUploadEvent(event: HttpEvent<any>, file: File): void {
    const fileKey = UploadButtonComponent.getFileKey(file);

    switch (event.type) {
      case HttpEventType.Sent:
        this.uploadedBytesByFile[fileKey] = 0;
        this.updateGlobalProgress();
        break;

      case HttpEventType.UploadProgress:
        this.uploadedBytesByFile[fileKey] = event.loaded ?? 0;
        this.updateGlobalProgress();
        break;

      case HttpEventType.Response:
        this.uploadedBytesByFile[fileKey] = file.size;
        this.updateGlobalProgress();
        break;
    }
  }

  private updateGlobalProgress(): void {
    const uploadedBytes = Object.values(this.uploadedBytesByFile)
      .reduce((sum, value) => sum + value, 0);

    this.progress = this.totalBytes > 0
      ? Math.round((uploadedBytes / this.totalBytes) * 100)
      : 0;
  }

  private static eventMessage(event: HttpEvent<any>, file: File): File {
    let message
    switch (event.type) {
      case HttpEventType.Sent:
        message = `Uploading file "${file.name}" of size ${file.size}.`;
        break;
      case HttpEventType.UploadProgress:
        message = `File "${file.name}" upload in progress.`;
        break;
      case HttpEventType.Response:
        message = `File "${file.name}" was completely uploaded!`;
        break;
      default:
        message = `File "${file.name}" surprising upload event: ${event.type}.`;
    }
    return file;
  }

  private handleError(error: any): void {
    console.error(error);
    this.progress = 0;
    this.isLoading = false;
    this.totalBytes = 0;
    this.uploadedBytesByFile = {};
  }

  private static getFileKey(file: File): string {
    return `${file.name}_${file.size}_${file.lastModified}`;
  }
}
